/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useRef, Fragment } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import {
  Card,
  Space,
  Typography,
  Tag,
  Spin,
  Popover,
  Image,
  Dropdown,
  Button,
} from "antd";
import { CloseCircleOutlined, MoreOutlined } from "@ant-design/icons";
import DayOffIcon from "../../../../atoms/CustomIcons/DayOffIcon";
import FlagIcon from "../../../../atoms/CustomIcons/FlagIcon";
import HomeIcon from "../../../../atoms/CustomIcons/HomeIcon";
import ChangeShift from "../../../../atoms/CustomIcons/ChangeShift";
import RescheduleIcon from "../../../../atoms/CustomIcons/RescheduleIcon";
import MoveIcon from "../../../../atoms/CustomIcons/MoveIcon";
import RouteCalculate from "../../../../atoms/CustomIcons/RouteCalculate";
import TimeSheetIcon from "../../../../atoms/CustomIcons/TimeSheetIcon";
import UnscheduleIcon from "../../../../atoms/CustomIcons/UnscheduleIcon";
import ConfirmIcon from "../../../../atoms/CustomIcons/ConfirmIcon";
import RouteIcon from "../../../../atoms/CustomIcons/RouteIcon";
import { PopUp } from "../../../../atoms/PopUp";
import MakeDayOff from "../MakeDayOff";
import EditStartFinishPoint from "../EditStartFinishPoint";
import MoveCrewScheduleBy from "../MoveCrewScheduleBy";
import ChangeShiftYardTime from "../ChangeShiftYardTime";
import RescheduleVisits from "../RescheduleVisits";
import ConfirmDelete from "../../../../atoms/ConfirmDelete";
import SuccessMessage from "../../../../atoms/Toasts/SuccessMessage";
import HourCard from "../KanbanBoard/HourCard";
import CrewCard from "../KanbanBoard/CrewCard";

dayjs.extend(duration);

interface WeatherCondition {
  text: string;
  icon: string;
  code: number;
}

interface DayForecast {
  maxtemp_c: number;
  maxtemp_f: number;
  mintemp_c: number;
  mintemp_f: number;
  avgtemp_c: number;
  avgtemp_f: number;
  condition: WeatherCondition;
  daily_will_it_rain: number;
  daily_chance_of_rain: number;
  daily_will_it_snow: number;
  daily_chance_of_snow: number;
  maxwind_kph: number;
  avghumidity: number;
}

interface ForecastDay {
  date: string;
  date_epoch: number;
  day: DayForecast;
}

interface Forecast {
  forecastday: ForecastDay[];
}

interface Location {
  name: string;
  region: string;
  country: string;
  lat: number;
  lon: number;
  tz_id: string;
  localtime_epoch: number;
  localtime: string;
}

interface WeatherApiResponse {
  location: Location;
  current: unknown;
  forecast?: Forecast;
  forecastday?: ForecastDay[];
}

interface Coordinates {
  lat: number;
  lng: number;
}

interface VisitExtendedProps {
  type: "visit" | "drive";
  durationMinutes?: number;
  driveTimeMinutes?: number;
  location?: string;
  nextVisitLocation?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
}

interface FullCalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  date?: string;
  allDay?: boolean;
  editable?: boolean;
  origin?: Coordinates;
  extendedProps?: VisitExtendedProps;
}

interface CustomDayHeaderProps {
  date: Date;
  events: FullCalendarEvent[];
  hoursData?: any;
  crewData?: any;
}

const roundToHalfOrWhole = (value: number) => {
  const decimal = value % 1;

  if (decimal === 0) return value;
  if (decimal <= 0.5) return Math.floor(value) + 0.5;
  return Math.ceil(value);
};

export default function CustomDayHeader({
  date,
  hoursData,
  crewData,
}: CustomDayHeaderProps) {
  const dayjsDate = dayjs(date);
  const dayOfWeek = dayjsDate.format("ddd");
  const dayOfMonth = dayjsDate.format("DD");

  const isWeekend = dayjsDate.day() === 0 || dayjsDate.day() === 6; // Sunday is 0, Saturday is 6

  const eventsForThisDay = hoursData?.filter((event: any) => {
    return dayjs(event.date).isSame(dayjsDate, "day");
  });

  let totalScheduledMinutes = 0;
  eventsForThisDay?.forEach((event: any) => {
    if (event.hours) {
      totalScheduledMinutes += event.hours;
    }
  });

  let totalScheduledHours = 0;
  if (totalScheduledMinutes > 0) {
    const rawHours = totalScheduledMinutes / 60;
    totalScheduledHours = roundToHalfOrWhole(rawHours);
  }

  const [weather, setWeather] = useState<{
    high: number;
    low: number;
    icon: string;
    precipitationChance?: number;
    windKph?: number;
    humidity?: number;
  } | null>(null);
  const [weatherLoading, setWeatherLoading] = useState<boolean>(true);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const [confirmAllWO, setConfirmAllWO] = useState(false);
  const [rescheduleVisitModal, setRescheduleVisitModal] = useState(false);
  const [editStartPointModal, setEditStartPointModal] = useState(false);
  const [editFinishPointModal, setEditFinishPointModal] = useState(false);
  const [changeShiftYardTimeModal, setChangeShiftYardTimeModal] =
    useState(false);
  const [moveCrewScheduleByModal, setMoveCrewScheduleByModal] = useState(false);
  const [makeDayOffModal, setMakeDayOffModal] = useState(false);

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
          }}
        >
          <RouteIcon /> Show route on a map
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
            setConfirmAllWO(true);
          }}
        >
          <ConfirmIcon /> Confirm all work orders
        </Button>
      ),
    },
    {
      key: "3",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
            setRescheduleVisitModal(true);
          }}
        >
          <RescheduleIcon /> Reschedule remaining visits
        </Button>
      ),
    },
    {
      key: "4",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
          }}
        >
          <UnscheduleIcon /> Unschedule all remaining visits
        </Button>
      ),
    },
    {
      key: "5",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
          }}
        >
          <TimeSheetIcon /> Open timesheet
        </Button>
      ),
    },
    {
      key: "6",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
          }}
        >
          <FlagIcon /> Edit finish point
        </Button>
      ),
    },
  ];
  const itemsFuture = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            // if (dropdownRef.current) {
            //   dropdownRef?.current?.click();
            // }
          }}
        >
          <RouteCalculate /> Calculate route
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            // if (dropdownRef.current) {
            //   dropdownRef?.current?.click();
            // }
          }}
        >
          <CloseCircleOutlined /> Discard unpublished changes
        </Button>
      ),
    },
    {
      key: "3",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
            setRescheduleVisitModal(true);
          }}
        >
          <MoveIcon /> Reschedule the whole day
        </Button>
      ),
    },
    {
      key: "4",
      label: (
        <Popover
          arrow={false}
          content={
            <>
              <Button type="text" className="no-hover">
                1 day forward
              </Button>
              <Button type="text" className="no-hover">
                2 days forward
              </Button>
              <Button
                type="text"
                className="no-hover"
                onClick={(e) => {
                  e.stopPropagation();
                  if (dropdownRef.current) {
                    dropdownRef?.current?.click();
                  }
                  setMoveCrewScheduleByModal(true);
                }}
              >
                Custom period ...
              </Button>
            </>
          }
          // trigger="click"
          placement="rightTop"
          getPopupContainer={(trigger) =>
            trigger.parentElement || document.body
          }
        >
          <Button type="text" className="no-hover">
            <RescheduleIcon /> Move crew’s schedule by ...
          </Button>
        </Popover>
      ),
    },
    {
      key: "5",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
            setChangeShiftYardTimeModal(true);
          }}
        >
          <ChangeShift /> Change shift & yard time
        </Button>
      ),
    },
    {
      key: "6",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
            setEditStartPointModal(true);
          }}
        >
          <HomeIcon /> Edit start point
        </Button>
      ),
    },
    {
      key: "7",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
            setEditFinishPointModal(true);
          }}
        >
          <FlagIcon /> Edit finish point
        </Button>
      ),
    },
    {
      key: "8",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
            setMakeDayOffModal(true);
          }}
        >
          <DayOffIcon /> Make a day-off
        </Button>
      ),
    },
  ];

  // const [crewCount, setCrewCount] = useState<number | null>(null);
  const today = dayjs();
  const selectedDate = dayjs(date).format("YYYY-MM-DD");
  const comparisonDate = dayjs(selectedDate);

  const fetchMockData = async () => {
    const API_KEY = "10a1bc62960a433f9ce91539252108";
    const LOCATION_QUERY = "Dallas";

    const fetchWeatherData = async () => {
      setWeatherLoading(true);
      let API_ENDPOINT = "";
      const targetDateStr = dayjsDate.format("YYYY-MM-DD");
      const today = dayjs();
      const diffDays = dayjsDate.diff(today, "day");

      if (diffDays >= 0 && diffDays <= 9) {
        API_ENDPOINT = `https://api.weatherapi.com/v1/forecast.json?key=${API_KEY}&q=${LOCATION_QUERY}&days=14`;
      } else if (dayjsDate.isBefore(today, "day")) {
        API_ENDPOINT = `https://api.weatherapi.com/v1/history.json?key=${API_KEY}&q=${LOCATION_QUERY}&dt=${targetDateStr}`;
      } else {
        setWeather(null);
        setWeatherLoading(false);
        console.warn(
          `Weather data not available for date: ${targetDateStr} (too far in future).`
        );
        return;
      }

      try {
        const response = await fetch(API_ENDPOINT);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: WeatherApiResponse = await response.json();

        let forecastForDay: DayForecast | undefined;

        if (data.forecast && data.forecast.forecastday) {
          forecastForDay = data.forecast.forecastday.find(
            (fd) => fd.date === targetDateStr
          )?.day;
        } else if (data.forecastday && data.forecastday.length > 0) {
          forecastForDay = data.forecastday?.[0].day;
        }

        if (forecastForDay) {
          const highTemp = forecastForDay.maxtemp_f;
          const lowTemp = forecastForDay.mintemp_f;
          const weatherIcon = forecastForDay.condition?.icon;
          const precipitationChance = forecastForDay.daily_chance_of_rain;
          const windKph = forecastForDay.maxwind_kph;
          const humidity = forecastForDay.avghumidity;

          setWeather({
            high: highTemp,
            low: lowTemp,
            icon: weatherIcon,
            precipitationChance,
            windKph,
            humidity,
          });
        } else {
          console.warn(`No weather data found for date: ${targetDateStr}`);
          setWeather(null);
        }
      } catch (error) {
        console.error("Failed to fetch weather data:", error);
        setWeather(null);
      } finally {
        setWeatherLoading(false);
      }
    };

    fetchWeatherData();
    // const mockCrew = 4;
    // const mockCompletedHours = totalScheduledHours;
    // const mockTotalHours = mockCrew * 8;

    // setHoursData({ completed: mockCompletedHours, total: mockTotalHours });
    // setCrewCount(mockCrew);
  };

  const btnConfirmAllWorkOrders = async () => {
    SuccessMessage("Work orders confirmed");
    setConfirmAllWO(false);
  };

  const btnRescheduleVisits = async () => {
    SuccessMessage("Visits rescheduled");
    setRescheduleVisitModal(false);
  };
  const btnChangeShiftYardTime = async () => {
    SuccessMessage("Shift start time changed");
    setChangeShiftYardTimeModal(false);
  };
  const btnMoveCrewScheduleBy = async () => {
    SuccessMessage("Crew schedule moved");
    setMoveCrewScheduleByModal(false);
  };
  const btnEditStartPoint = async () => {
    SuccessMessage("Start point changed");
    setEditStartPointModal(false);
  };
  const btnEditFinishPoint = async () => {
    SuccessMessage("Finish point changed");
    setEditFinishPointModal(false);
  };
  const btnMakeDayOff = async () => {
    SuccessMessage("Day off scheduled");
    setMakeDayOffModal(false);
  };

  useEffect(() => {
    fetchMockData();
  }, []);

  const weatherDetailsContent = (
    <div>
      <Typography.Title level={4} style={{ margin: "0 0 4px 0" }}>
        Weather details
      </Typography.Title>
      {weather ? (
        <Space direction="vertical" size={12}>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Image
              src={weather.icon}
              alt="Weather Icon"
              width={32}
              preview={false}
            />
            <Tag className="default_tag">
              Highest:{" "}
              <span style={{ fontWeight: "600", color: "#F06400" }}>
                {weather.high}°
              </span>
            </Tag>
            <Tag className="default_tag">
              Lowest:{" "}
              <span style={{ fontWeight: "600", color: "#2098EE" }}>
                {weather.low}°
              </span>
            </Tag>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ width: "90px" }}>
              <Typography.Text
                style={{
                  fontWeight: "500",
                  fontSize: "12px",
                  color: "#151718",
                }}
              >
                Precipitation
              </Typography.Text>
              <br />
              <div className="detailsData">
                <Image
                  src="/images/schedule/precipitation.svg"
                  preview={false}
                />
                <Typography.Text>
                  {weather.precipitationChance || 0} <span>%</span>
                </Typography.Text>
              </div>
            </div>
            <div style={{ width: "90px" }}>
              <Typography.Text
                style={{
                  fontWeight: "500",
                  fontSize: "12px",
                  color: "#151718",
                }}
              >
                Wind
              </Typography.Text>
              <br />
              <div className="detailsData">
                <Image src="/images/schedule/windy.svg" preview={false} />
                <Typography.Text>
                  {weather.windKph || 0} <span>km/h</span>
                </Typography.Text>
              </div>
            </div>
            <div style={{ width: "90px" }}>
              <Typography.Text
                style={{
                  fontWeight: "500",
                  fontSize: "12px",
                  color: "#151718",
                }}
              >
                Humidity
              </Typography.Text>
              <br />
              <div className="detailsData">
                <Image src="/images/schedule/humidity.svg" preview={false} />
                <Typography.Text>
                  {weather.humidity || 0}
                  <span>%</span>
                </Typography.Text>
              </div>
            </div>
          </div>
        </Space>
      ) : (
        <Typography.Text type="secondary">
          No detailed weather data available.
        </Typography.Text>
      )}
    </div>
  );

  const confirmAllWorkOrders = {
    visibility: confirmAllWO,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setConfirmAllWO(false)}
        heading="Confirm all work orders for this route"
        paragraph={
          <Fragment>
            This will mark all work orders completed by the crew as confirmed.
            <br />
            <br />
            Are you sure you want to proceed?
          </Fragment>
        }
        btnText="Confirm"
        loading={false}
        isArchivePopup
        btnApi={btnConfirmAllWorkOrders}
      />
    ),
    width: 480,
    onCancel: () => setConfirmAllWO(false),
  };

  const rescheduleVisit = {
    visibility: rescheduleVisitModal,
    content: (
      <RescheduleVisits
        onClose={() => setRescheduleVisitModal(false)}
        btnApi={btnRescheduleVisits}
        loading={false}
      />
    ),
    width: 360,
    onCancel: () => setRescheduleVisitModal(false),
  };
  const changeShiftYardTime = {
    visibility: changeShiftYardTimeModal,
    content: (
      <ChangeShiftYardTime
        onClose={() => setChangeShiftYardTimeModal(false)}
        btnApi={btnChangeShiftYardTime}
        loading={false}
      />
    ),
    width: 385,
    onCancel: () => setChangeShiftYardTimeModal(false),
  };
  const moveCrewScheduleBy = {
    visibility: moveCrewScheduleByModal,
    content: (
      <MoveCrewScheduleBy
        onClose={() => setMoveCrewScheduleByModal(false)}
        btnApi={btnMoveCrewScheduleBy}
        loading={false}
        selectedDate={selectedDate}
      />
    ),
    width: 480,
    onCancel: () => setMoveCrewScheduleByModal(false),
  };
  const editStartPoint = {
    visibility: editStartPointModal,
    content: (
      <EditStartFinishPoint
        onClose={() => setEditStartPointModal(false)}
        btnApi={btnEditStartPoint}
        loading={false}
        title="Edit start point"
        paragraph="By default, the crew starts their day at their assigned yard. When needed, you can change it to a different location."
      />
    ),
    width: 480,
    onCancel: () => setEditStartPointModal(false),
  };
  const editFinishPoint = {
    visibility: editFinishPointModal,
    content: (
      <EditStartFinishPoint
        onClose={() => setEditFinishPointModal(false)}
        btnApi={btnEditFinishPoint}
        loading={false}
        title="Edit finish point"
        paragraph="By default, the crew finishes their day at their assigned yard. When needed, you can change it to a different location."
      />
    ),
    width: 480,
    onCancel: () => setEditFinishPointModal(false),
  };
  const makeDayOff = {
    visibility: makeDayOffModal,
    content: (
      <MakeDayOff
        onClose={() => setMakeDayOffModal(false)}
        btnApi={btnMakeDayOff}
        loading={false}
        selectedDate={selectedDate}
      />
    ),
    width: 480,
    onCancel: () => setMakeDayOffModal(false),
  };

  console.log("totalScheduledHours", totalScheduledHours);

  return (
    <Card className="calendarHeaderCard">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "8px",
        }}
      >
        <Tag className="headerTag" style={{ height: "26px" }}>
          <span>{dayOfWeek}</span>
          <span style={{ fontWeight: 700 }}>{dayOfMonth}</span>
        </Tag>
        <div style={{ display: "flex", marginLeft: "auto" }}>
          <Popover
            content={weatherDetailsContent}
            title={null}
            trigger="click"
            placement="bottomRight"
            overlayClassName="weatherPopoverOpen"
          >
            <Space size={4} style={{ cursor: "pointer" }}>
              {weatherLoading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "32px",
                    height: "32px",
                  }}
                >
                  <Spin size="small" />
                </div>
              ) : weather ? (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "column",
                      gap: "1px",
                    }}
                  >
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: "11px",
                        color: "#151718",
                        fontWeight: "400",
                      }}
                    >
                      H: {weather.high}°
                    </Typography.Text>

                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: "11px",
                        color: "#151718",
                        fontWeight: "400",
                      }}
                    >
                      L: {weather.low}°
                    </Typography.Text>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "32px",
                      height: "32px",
                    }}
                  >
                    <Image
                      src={weather.icon}
                      alt="Weather Icon"
                      width={32}
                      preview={false}
                    />
                  </div>
                </>
              ) : (
                <div style={{ height: "36px" }}>
                  <Image
                    src="/images/schedule/not-available-icon.svg"
                    preview={false}
                  />
                </div>
              )}
            </Space>
          </Popover>
          <div
            className="column_actions"
            style={{ width: "24px", height: "24px" }}
          >
            <Dropdown
              menu={{
                items: comparisonDate.isAfter(today, "day")
                  ? itemsFuture
                  : items,
              }}
              trigger={["click"]}
              placement="bottomLeft"
              className="actionButton"
            >
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onKeyDown={(e) => e.stopPropagation()}
                className="white_border"
                icon={<MoreOutlined />}
                ref={dropdownRef}
                style={{ width: "100%", height: "100%" }}
              />
            </Dropdown>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "10px",
          paddingLeft: "6px",
          paddingRight: "6px",
        }}
      >
        <HourCard
          isWeekend={isWeekend}
          hoursData={hoursData}
          totalScheduledHours={32}
          // selectedDate={date}
          // index={crewData?.length > 0 && crewData?.[0]?.index}
        />
        <CrewCard
          isWeekend={isWeekend}
          crewCount={4}
          column={crewData?.length > 0 && crewData?.[0]}
          selectedDate={date}
          index={crewData?.length > 0 && crewData?.[0]?.index}
        />
      </div>
      <PopUp {...confirmAllWorkOrders} />
      <PopUp {...rescheduleVisit} />
      <PopUp {...changeShiftYardTime} />
      <PopUp {...moveCrewScheduleBy} />
      <PopUp {...editStartPoint} />
      <PopUp {...editFinishPoint} />
      <PopUp {...makeDayOff} />
    </Card>
  );
}
